(function () {
  "use strict";

  const root = window.LegitGrails = window.LegitGrails || {};
  const config = root.faroConfig || {};
  let sdkLoadPromise = null;
  let cartTracePromise = null;
  let faroInstance = null;
  let faroEnabledByConsent = false;
  let initPromise = null;
  const traceId = createId("trace");
  let pendingTelemetry = [];
  const PENDING_TELEMETRY_LIMIT = 50;
  const PRE_FARO_TELEMETRY_QUEUE = "preFaroTelemetry";
  const recentEventTimestamps = new Map();
  const ERROR_DEDUP_WINDOW_MS = 10000;
  const BOT_OR_CRAWLER_USER_AGENT_PATTERN =
    /meta-externalads|facebookexternalhit|adsbot|googlebot|bingbot|crawler|spider|lighthouse/i;
  const THIRD_PARTY_MONITORING_NOISE_PATTERNS = [
    /sessions\.bugsnag\.com/i,
    /\[bugsnag\]\s*session failed to send/i,
    /\[bugsnag\]\s*loaded!/i,
    /Failed to execute 'importScripts'.*\/web-pixels\/strict\/app\/web-pixel-/i,
    /\/web-pixels\/strict\/app\/web-pixel-/i,
    /window\.webkit\.messageHandlers/i,
  ];

  /**
   * Creates a trace-friendly client-side identifier.
   *
   * @param {string} prefix
   * @returns {string}
   */
  function createId(prefix) {
    const cryptoApi = window.crypto || window.msCrypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
      return prefix + "_" + cryptoApi.randomUUID();
    }

    return prefix + "_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 12);
  }

  function asPlainObject(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  }

  function truncate(value, maxLength) {
    const normalized = String(value == null ? "" : value);
    if (normalized.length <= maxLength) return normalized;
    return normalized.slice(0, maxLength) + "...";
  }

  function isIdentifierKey(key) {
    return /(^|_)(id|token|key)$/i.test(key) ||
      /(Id|ID|Token|Key)$/.test(key) ||
      key === "sessionStorageKey";
  }

  function redactSensitiveText(value, key) {
    let redacted = truncate(String(value == null ? "" : value), 500)
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[redacted-email]")
      .replace(/(token|api_key|password|secret)=([^&\s]+)/gi, "$1=***");

    if (isIdentifierKey(key || "")) {
      return redacted;
    }

    return redacted.replace(/(^|[^\w])(\+?\d[\d\s().-]{7,}\d)(?=$|[^\w])/g, function (match, prefix, candidate) {
      const digitCount = candidate.replace(/\D/g, "").length;
      const hasPhoneSignal = /^\+/.test(candidate) || /[\s().-]/.test(candidate);
      return digitCount >= 8 && hasPhoneSignal ? prefix + "[redacted-phone]" : match;
    });
  }

  function sanitizeUrl(value) {
    if (!value) return value;

    try {
      const url = new URL(String(value), window.location.origin);
      ["token", "api_key", "password", "secret"].forEach(function (param) {
        if (url.searchParams.has(param)) {
          url.searchParams.set(param, "***");
        }
      });
      return url.toString();
    } catch (error) {
      return redactSensitiveText(value);
    }
  }

  function sanitizeContext(context) {
    const source = asPlainObject(context);
    const safe = {};
    const blockedKeys = {
      fileName: true,
      filename: true,
      rawFileName: true,
      publicPhotoUrl: true,
      uploadedFileUrl: true,
      uploadUrl: true,
      tusUploadUrl: true,
      email: true,
      phone: true,
      name: true,
      cookie: true,
      cookies: true,
    };

    Object.keys(source).forEach(function (key) {
      if (blockedKeys[key]) return;

      const value = source[key];
      if (value == null) return;

      if (typeof value === "string") {
        if (/url$/i.test(key) || key === "href") {
          safe[key] = sanitizeUrl(value);
          return;
        }

        safe[key] = redactSensitiveText(value, key);
        return;
      }

      if (typeof value === "number" || typeof value === "boolean") {
        safe[key] = value;
        return;
      }

      if (value instanceof Error) {
        safe[key] = redactSensitiveText(value.message, key);
        return;
      }

      try {
        safe[key] = redactSensitiveText(JSON.stringify(value), key);
      } catch (error) {
        safe[key] = "[unserializable]";
      }
    });

    return safe;
  }

  function stringifyAttributeValue(value) {
    if (value == null) return null;
    if (value instanceof Error) return redactSensitiveText(value.message);
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);

    try {
      return redactSensitiveText(JSON.stringify(value));
    } catch (error) {
      return "[unserializable]";
    }
  }

  function sanitizeAttributes(context) {
    const safeContext = sanitizeContext(context);
    const attributes = {};

    Object.keys(safeContext).forEach(function (key) {
      const value = stringifyAttributeValue(safeContext[key]);
      if (value != null) {
        attributes[key] = value;
      }
    });

    return attributes;
  }

  function getComparableMessageFromPayload(payload) {
    if (!payload) return "";
    if (typeof payload === "string") return payload;

    if (Array.isArray(payload)) {
      return payload.map(function (part) {
        return typeof part === "string" ? part : JSON.stringify(part);
      }).join(" ");
    }

    if (typeof payload === "object") {
      if (typeof payload.value === "string") return payload.value;
      if (typeof payload.message === "string") return payload.message;
      if (typeof payload.type === "string") return payload.type;

      try {
        return JSON.stringify(payload);
      } catch (error) {
        return "";
      }
    }

    return String(payload);
  }

  function getPathFromItem(item) {
    return item &&
      item.payload &&
      item.payload.context &&
      typeof item.payload.context.path === "string"
      ? item.payload.context.path
      : "";
  }

  function getPageUrlFromItem(item) {
    return item &&
      item.meta &&
      item.meta.page &&
      typeof item.meta.page.url === "string"
      ? item.meta.page.url
      : "";
  }

  function getDedupFingerprint(item) {
    if (!item || typeof item !== "object") return null;

    try {
      return JSON.stringify({
        message: getComparableMessageFromPayload(item.payload),
        path: getPathFromItem(item),
        pageUrl: getPageUrlFromItem(item),
      });
    } catch (error) {
      return null;
    }
  }

  function shouldDropDuplicateEvent(item) {
    const fingerprint = getDedupFingerprint(item);
    if (!fingerprint) return false;

    const now = Date.now();
    recentEventTimestamps.forEach(function (timestamp, key) {
      if (now - timestamp > ERROR_DEDUP_WINDOW_MS) {
        recentEventTimestamps.delete(key);
      }
    });

    const previousTimestamp = recentEventTimestamps.get(fingerprint);
    recentEventTimestamps.set(fingerprint, now);

    return typeof previousTimestamp === "number" && now - previousTimestamp < ERROR_DEDUP_WINDOW_MS;
  }

  function shouldDropThirdPartyNoise(item) {
    try {
      const raw = JSON.stringify(item);
      if (!raw) return false;
      return THIRD_PARTY_MONITORING_NOISE_PATTERNS.some(function (pattern) {
        return pattern.test(raw);
      });
    } catch (error) {
      return false;
    }
  }

  function isBotOrCrawler() {
    return BOT_OR_CRAWLER_USER_AGENT_PATTERN.test(window.navigator.userAgent || "");
  }

  function beforeSend(item) {
    if (isBotOrCrawler()) return null;
    if (!faroEnabledByConsent) return null;
    if (shouldDropThirdPartyNoise(item)) return null;
    if (shouldDropDuplicateEvent(item)) return null;

    if (item && item.meta && item.meta.page && item.meta.page.url) {
      item.meta.page.url = sanitizeUrl(item.meta.page.url);
    }

    if (item && item.meta && item.meta.page && item.meta.page.attributes) {
      item.meta.page.attributes = sanitizeAttributes(item.meta.page.attributes);
    }

    if (item && item.payload && typeof item.payload === "object") {
      let strPayload = "";
      try {
        strPayload = JSON.stringify(item.payload);
      } catch (error) {
        strPayload = "";
      }

      if (/password|secret/i.test(strPayload)) {
        return null;
      }

      const isExceptionPayload =
        item.payload.stacktrace ||
        typeof item.payload.value === "string" ||
        typeof item.payload.type === "string";

      if (isExceptionPayload) {
        item.payload.context = sanitizeAttributes(Object.assign(
          {},
          getTraceContext(),
          asPlainObject(item.payload.context),
        ));
      } else if (item.payload.context) {
        item.payload.context = sanitizeAttributes(item.payload.context);
      }
    }

    return item;
  }

  function getSdkGlobal() {
    const globalName = config.sdkGlobal || "GrafanaFaroWebSdk";
    return window[globalName] || null;
  }

  function loadFaroSdk() {
    if (sdkLoadPromise) return sdkLoadPromise;

    sdkLoadPromise = new Promise(function (resolve, reject) {
      if (!config.enabled) {
        resolve(null);
        return;
      }

      const existingSdk = getSdkGlobal();
      if (existingSdk) {
        resolve(existingSdk);
        return;
      }

      if (!config.sdkUrl) {
        reject(new Error("[Faro] Missing SDK URL"));
        return;
      }

      const existingScript = document.querySelector("script[data-lg-faro-sdk]");
      if (existingScript) {
        existingScript.addEventListener("load", function () {
          resolve(getSdkGlobal());
        }, { once: true });
        existingScript.addEventListener("error", function () {
          reject(new Error("[Faro] SDK failed to load"));
        }, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = config.sdkUrl;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.referrerPolicy = "no-referrer";
      script.setAttribute("data-lg-faro-sdk", "true");

      script.addEventListener("load", function () {
        resolve(getSdkGlobal());
      }, { once: true });

      script.addEventListener("error", function () {
        reject(new Error("[Faro] SDK failed to load"));
      }, { once: true });

      document.head.appendChild(script);
    }).catch(function () {
      return null;
    });

    return sdkLoadPromise;
  }

  function loadCartTraceContext() {
    if (cartTracePromise) return cartTracePromise;

    cartTracePromise = fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/") + "cart.js", {
      credentials: "same-origin",
    })
      .then(function (response) {
        if (!response.ok) throw new Error("[Faro] Cart context request failed");
        return response.json();
      })
      .then(function (cart) {
        return {
          cartToken: cart && cart.token ? sanitizeCartToken(cart.token) : null,
          cartItemCount: cart && Number.isFinite(cart.item_count) ? cart.item_count : null,
        };
      })
      .catch(function () {
        return {
          cartToken: null,
          cartItemCount: config.shopify ? config.shopify.cartItemCount : null,
        };
      });

    return cartTracePromise;
  }

  function sanitizeCartToken(value) {
    return String(value || "").split("?")[0] || null;
  }

  /**
   * Returns sanitized Shopify/page identifiers attached to Faro errors.
   *
   * @returns {object}
   */
  function getTraceContext() {
    const shopify = asPlainObject(config.shopify);
    const app = asPlainObject(config.app);
    return sanitizeContext({
      traceId: traceId,
      environment: app.environment || null,
      requestId: shopify.requestId || null,
      customerId: shopify.customerId || null,
      cartToken: root.cartTraceContext ? root.cartTraceContext.cartToken : null,
      cartItemCount: root.cartTraceContext ? root.cartTraceContext.cartItemCount : shopify.cartItemCount,
      shopDomain: shopify.shopDomain,
      host: shopify.host,
      path: shopify.path || window.location.pathname,
      pageUrl: window.location.href,
      pageType: shopify.pageType,
      locale: shopify.locale,
    });
  }

  function setFaroUser() {
    if (!faroInstance || !faroInstance.api || typeof faroInstance.api.setUser !== "function") return;

    const shopify = asPlainObject(config.shopify);
    if (!shopify.customerId) return;

    faroInstance.api.setUser({
      id: String(shopify.customerId),
      attributes: {
        shopDomain: shopify.shopDomain ? String(shopify.shopDomain) : "",
      },
    });
  }

  function setFaroPage() {
    if (!faroInstance || !faroInstance.api) return;

    if (typeof faroInstance.api.setView === "function") {
      faroInstance.api.setView({
        name: (config.shopify && config.shopify.path) || window.location.pathname,
      });
    }

    if (typeof faroInstance.api.setPage === "function") {
      faroInstance.api.setPage({
        id: traceId,
        attributes: sanitizeAttributes(getTraceContext()),
      });
    }
  }

  /**
   * Initializes the Faro runtime after collection is allowed.
   *
   * Today collection is enabled by config.collectByDefault. After Cookie
   * Consent migration this same function will be called only after the
   * effective performance consent is granted through setFaroConsent(true).
   *
   * @returns {Promise<object|null>}
   */
  function initFaro() {
    if (faroInstance) return Promise.resolve(faroInstance);
    if (initPromise) return initPromise;
    if (!faroEnabledByConsent || !config.enabled || !config.collectorUrl) {
      return Promise.resolve(null);
    }

    initPromise = Promise.all([
      loadFaroSdk(),
      loadCartTraceContext(),
    ]).then(function (results) {
      const sdk = results[0];
      root.cartTraceContext = results[1];

      if (!sdk || !faroEnabledByConsent || !config.enabled || !config.collectorUrl) {
        initPromise = null;
        return null;
      }

      if (typeof sdk.initializeFaro !== "function") {
        initPromise = null;
        return null;
      }

      faroInstance = sdk.initializeFaro({
        url: config.collectorUrl,
        app: config.app || {
          name: "legitgrails-shopify-theme",
        },
        ignoreErrors: [/Script error/i, /Unknown error/i],
        instrumentations: typeof sdk.getWebInstrumentations === "function"
          ? sdk.getWebInstrumentations({ captureConsole: false })
          : undefined,
        beforeSend: beforeSend,
      });

      setFaroUser();
      setFaroPage();
      flushPreFaroTelemetry();
      flushPendingTelemetry();

      return faroInstance;
    }).catch(function () {
      initPromise = null;
      return null;
    });

    return initPromise;
  }

  /**
   * Queues telemetry while the Faro SDK is loading after consent is enabled.
   *
   * @param {{type: "event"|"error", name?: string, attributes?: object, domain?: string, error?: *, context?: object}} item
   * @returns {void}
   */
  function queueTelemetry(item) {
    if (!faroEnabledByConsent) return;
    pendingTelemetry.push(item);
    if (pendingTelemetry.length > PENDING_TELEMETRY_LIMIT) {
      pendingTelemetry.shift();
    }
    initFaro();
  }

  /**
   * Synchronizes the synchronous bridge with the current Faro consent state.
   *
   * @param {boolean} isAllowed
   * @returns {void}
   */
  function setBridgeCollectionAllowed(isAllowed) {
    const bridge = root.telemetryBridge;
    if (bridge && typeof bridge.setCollectionAllowed === "function") {
      bridge.setCollectionAllowed(isAllowed);
    } else if (!isAllowed && Array.isArray(root[PRE_FARO_TELEMETRY_QUEUE])) {
      root[PRE_FARO_TELEMETRY_QUEUE].length = 0;
    }
  }

  /**
   * Moves telemetry captured by the synchronous bridge into the Faro facade.
   *
   * @returns {void}
   */
  function flushPreFaroTelemetry() {
    const bridge = root.telemetryBridge;
    const queuedTelemetry = bridge && typeof bridge.drainQueue === "function"
      ? bridge.drainQueue()
      : Array.isArray(root[PRE_FARO_TELEMETRY_QUEUE])
        ? root[PRE_FARO_TELEMETRY_QUEUE].splice(0)
        : [];

    queuedTelemetry.forEach(function (item) {
      if (!item || typeof item !== "object") return;

      if (item.type === "event") {
        pushEvent(item.name, item.attributes, item.domain);
      } else if (item.type === "error") {
        pushError(item.error, item.context);
      }
    });
  }

  /**
   * Flushes calls queued after the Faro facade exists but before SDK init ends.
   *
   * @returns {void}
   */
  function flushPendingTelemetry() {
    if (!pendingTelemetry.length || !faroInstance) return;

    const items = pendingTelemetry.slice();
    pendingTelemetry = [];

    items.forEach(function (item) {
      if (item.type === "event") {
        pushEvent(item.name, item.attributes, item.domain);
      } else if (item.type === "error") {
        pushError(item.error, item.context);
      }
    });
  }

  /**
   * Enables or pauses Faro collection according to effective consent.
   *
   * Current behavior calls this with true by default. Future Cookie Consent
   * integration should call it with the effective performance category value.
   *
   * @param {boolean} isEnabled
   * @returns {void}
   */
  function setFaroConsent(isEnabled) {
    faroEnabledByConsent = Boolean(isEnabled);
    setBridgeCollectionAllowed(faroEnabledByConsent);

    if (faroInstance && faroInstance.pause && faroInstance.unpause) {
      if (faroEnabledByConsent) {
        faroInstance.unpause();
      } else {
        faroInstance.pause();
      }
    }

    if (faroEnabledByConsent) {
      initFaro();
      flushPreFaroTelemetry();
    } else {
      pendingTelemetry = [];
    }
  }

  function normalizeError(error) {
    if (error instanceof Error) return error;
    if (typeof error === "string") return new Error(error);

    try {
      return new Error(JSON.stringify(error));
    } catch (serializeError) {
      return new Error(String(error));
    }
  }

  /**
   * Sends a custom event to Faro, or queues it while the SDK initializes.
   *
   * @param {string} name
   * @param {object} [attributes]
   * @param {string} [domain]
   * @returns {void}
   */
  function pushEvent(name, attributes, domain) {
    try {
      if (!faroInstance || !faroEnabledByConsent || !faroInstance.api || typeof faroInstance.api.pushEvent !== "function") {
        queueTelemetry({
          type: "event",
          name: name,
          attributes: attributes,
          domain: domain,
        });
        return;
      }

      const eventAttributes = sanitizeAttributes(attributes);
      faroInstance.api.pushEvent(String(name), eventAttributes, domain || "shopify_theme");
    } catch (error) {
      // Telemetry must never break the storefront.
    }
  }

  /**
   * Sends an exception to Faro with sanitized Shopify/upload context.
   *
   * @param {*} error
   * @param {object} [context]
   * @returns {void}
   */
  function pushError(error, context) {
    try {
      if (!faroInstance || !faroEnabledByConsent || !faroInstance.api || typeof faroInstance.api.pushError !== "function") {
        queueTelemetry({
          type: "error",
          error: error,
          context: context,
        });
        return;
      }

      faroInstance.api.pushError(normalizeError(error), {
        context: sanitizeAttributes(Object.assign({}, getTraceContext(), sanitizeContext(context))),
      });
    } catch (pushErrorFailure) {
      // Telemetry must never break the storefront.
    }
  }

  function isConsentCategoryExecutable(state, category) {
    if (!state || !state.categories || !state.categories[category]) return false;
    if (state.hasExplicitConsent) return true;
    return state.region === "ROW" || state.region === "USA_CA";
  }

  function bindFutureConsentStore() {
    const consentStore = root.cookieConsent;
    const category = config.futureConsentCategory || "performance";

    if (consentStore && typeof consentStore.getState === "function") {
      setFaroConsent(isConsentCategoryExecutable(consentStore.getState(), category));

      if (typeof consentStore.subscribe === "function") {
        consentStore.subscribe(function (state) {
          setFaroConsent(isConsentCategoryExecutable(state, category));
        });
      }

      return true;
    }

    return false;
  }

  root.telemetry = {
    loadSdk: loadFaroSdk,
    init: initFaro,
    setFaroConsent: setFaroConsent,
    pushEvent: pushEvent,
    pushError: pushError,
    getTraceContext: getTraceContext,
    createId: createId,
    isEnabled: function () {
      return Boolean(faroInstance && faroEnabledByConsent);
    },
  };

  if (config.collectByDefault) {
    setFaroConsent(true);
  } else if (!bindFutureConsentStore()) {
    setFaroConsent(false);
    document.addEventListener("legitgrails:cookie-consent-ready", bindFutureConsentStore);
  }
})();
